<template>
  <div :class="$style.modal">
    <h3 :class="$style.modal__title">次回面談予約</h3>
    <div :class="$style.modal__body">
      <div :class="[$style.modal__field, $style.field]">
        <p :class="$style.field__label">
          日程調整用公開URL または カレンダー設定URL または カレンダーGUID
        </p>
        <DolphinTextInput
          :class="$style.field__input"
          v-model="calendarGuidOrRelatedUrl"
        />
      </div>
      <div :class="[$style.modal__field, $style.field]">
        <p :class="$style.field__label">候補カレンダー名</p>
        <div :class="$style.field__value">
          <LoadingIndicator
            :class="$style.field__loading"
            v-if="calendarDetailLoading"
          /><span v-else>{{ candidateCalendarTitleStr ?? '未選択' }}</span>
        </div>
      </div>
      <div :class="$style.modal__note">
        ※通知先について:
        オカネコユーザーへの通知は、元の予約のカレンダーの所有プランナーとチャットが開設されていれば、そこのチャットに通知を送信します。<br />
        チャットが開設されていなければ、次の予約のカレンダーの所有プランナーとのチャットを開設し、開設したチャットに通知を送信します。<br />
        ※カレンダー選択後、予約ページを開いて日程を選択してください。
      </div>
    </div>
    <div :class="$style.modal__actions">
      <FlatButton variant="secondary" @click="emit('cancel')">
        閉じる
      </FlatButton>
      <FlatButton
        variant="primary"
        :disabled="!candidateCalendar"
        @click="openNextBookingPage"
      >
        予約ページを開く
      </FlatButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import { useAlert } from '@/libs/alertPortal'
import { version as uuidVersion, validate as uuidValidate } from 'uuid'
import { useFetchCalendarDetailForCheckLazyQuery } from './NextBookModal.generated.ts'

const props = defineProps<{
  bookingGUID: string
}>()

const emit = defineEmits<{
  (event: 'cancel'): void
}>()

const router = useRouter()
const route = useRoute('calendarSettingBooking')

const calendarGuidOrRelatedUrl = ref('')

const parsePathLastGuidFromURLString = (url: string): string | undefined => {
  const pathname = URL.parse(url)?.pathname

  if (!pathname) {
    return undefined
  }

  const lastSlashlessPathname = pathname.replace(/\/$/, '')
  return lastSlashlessPathname.split('/').slice(-1)[0]
}

const candidateCalendarGuid = computed((): string | undefined => {
  const candidateGuid =
    parsePathLastGuidFromURLString(calendarGuidOrRelatedUrl.value) ??
    calendarGuidOrRelatedUrl.value

  const validateUuidV7 = (str: string) =>
    uuidValidate(str) && uuidVersion(str) === 7

  if (!validateUuidV7(candidateGuid)) {
    return undefined
  }

  return candidateGuid
})

const {
  result: calendarDetailResult,
  load: calendarDetailLoad,
  refetch: calendarDetailRefetch,
  loading: calendarDetailLoading,
} = useFetchCalendarDetailForCheckLazyQuery(
  computed(() => ({
    guid: String(candidateCalendarGuid.value),
  })),
  {
    fetchPolicy: 'no-cache',
  }
)

const calendarDetailFetchOrRefetch = async () =>
  (await calendarDetailLoad(null, {
    guid: String(candidateCalendarGuid.value),
  })) ||
  (await calendarDetailRefetch({
    guid: String(candidateCalendarGuid.value),
  }))

watch(candidateCalendarGuid, async () => {
  if (!candidateCalendarGuid.value) {
    return
  }

  try {
    await calendarDetailFetchOrRefetch()
  } catch (e) {
    alert.error('エラーが発生しました。暫くしてからもう一度お試しください。')
    console.error(e)
  }
})

const candidateCalendar = computed(() => {
  if (
    !candidateCalendarGuid.value ||
    calendarDetailResult.value?.calendarDetail.__typename !==
      'CalendarDetailSuccess'
  ) {
    return
  }

  return calendarDetailResult.value?.calendarDetail.calendar
})

const candidateCalendarTitleStr = computed(() => {
  if (!calendarDetailResult.value) {
    return undefined
  }

  switch (calendarDetailResult.value.calendarDetail.__typename) {
    case 'CalendarDetailNotFound':
      return 'カレンダーが見つかりませんでした。入力値に誤りが無いか再確認してください。'
    case 'CalendarDetailPermissionDenied':
      return '入力されたカレンダーの使用権限がありません。現在のアカウントがカレンダー所有プランナーへ切り替え可能か確認してください。'
  }

  if (!candidateCalendar.value) {
    return undefined
  }

  const c = candidateCalendar.value
  return `${c.publicTitle}` + (c.privateTitle ? `(${c.privateTitle})` : '')
})

const alert = useAlert()

const openNextBookingPage = () => {
  if (!candidateCalendarGuid.value) {
    return
  }

  const entranceGUID = route.params.entranceGUID

  if (entranceGUID === '') {
    alert.error('エラーが発生しました')
    return
  }

  const routeData = router.resolve({
    name: 'nextBooking',
    params: {
      entranceGUID,
      previousBookingGUID: props.bookingGUID,
      calendarGUID: candidateCalendarGuid.value,
    },
  })

  window.open(routeData.href, '_blank')
}
</script>

<style lang="scss" module>
.modal {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__title {
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 8px;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__field {
    width: 100%;
  }

  &__note {
    color: $color-text-and-object-accent-red;
    font-size: 14px;
    line-height: 1.5;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 8px;
  }
}

.field {
  &__label {
    font-weight: bold;
    margin-bottom: 8px;
  }

  &__input {
    width: 100%;
  }

  &__value {
    display: flex;
    align-items: center;
  }

  &__loading {
    margin-left: 8px;
  }
}
</style>
